import { useEffect, useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/providers/AuthProvider';
import { supabase } from '@/integrations/supabase/client';
import {
  queueAction,
  getPendingActions,
  markActionSynced,
  markActionFailed,
} from '@/lib/offline-db';

export function useOfflineSync() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [syncingCount, setSyncingCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      toast({
        title: "Back online",
        description: "Syncing queued actions...",
      });
      syncQueuedActions();
    };

    const handleOffline = () => {
      setIsOnline(false);
      toast({
        title: "Offline mode",
        description: "Actions will be queued and synced when you're back online",
        variant: "destructive",
      });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Sync queued actions
  const syncQueuedActions = async () => {
    if (!user || !isOnline || isSyncing) return;

    setIsSyncing(true);
    const actions = await getPendingActions();
    setSyncingCount(actions.length);

    if (actions.length === 0) {
      setIsSyncing(false);
      return;
    }

    let successCount = 0;
    let failCount = 0;

    for (const action of actions) {
      try {
        // Execute the queued action
        switch (action.actionType) {
          case 'INSERT':
            await supabase.from(action.tableName as any).insert(action.payload);
            break;
          case 'UPDATE':
            if (action.recordId) {
              await supabase
                .from(action.tableName as any)
                .update(action.payload)
                .eq('id', action.recordId);
            }
            break;
          case 'DELETE':
            if (action.recordId) {
              await supabase
                .from(action.tableName as any)
                .delete()
                .eq('id', action.recordId);
            }
            break;
        }

        await markActionSynced(action.id);
        successCount++;
      } catch (error) {
        console.error('Failed to sync action:', error);
        await markActionFailed(action.id);
        failCount++;
      }
    }

    setIsSyncing(false);
    setSyncingCount(0);

    if (successCount > 0) {
      toast({
        title: "Sync complete",
        description: `${successCount} action(s) synced successfully${failCount > 0 ? `, ${failCount} failed` : ''}`,
      });
    }
  };

  // Queue an action when offline
  const queueOfflineAction = async (
    actionType: 'INSERT' | 'UPDATE' | 'DELETE',
    tableName: string,
    payload: any,
    recordId?: string
  ) => {
    if (!user) return;

    if (isOnline) {
      // Execute immediately if online
      try {
        switch (actionType) {
          case 'INSERT':
            await supabase.from(tableName as any).insert(payload);
            break;
          case 'UPDATE':
            if (recordId) {
              await supabase
                .from(tableName as any)
                .update(payload)
                .eq('id', recordId);
            }
            break;
          case 'DELETE':
            if (recordId) {
              await supabase.from(tableName as any).delete().eq('id', recordId);
            }
            break;
        }
      } catch (error) {
        console.error('Failed to execute action:', error);
        // Queue it for later if immediate execution fails
        await queueAction(user.id, actionType, tableName, payload, recordId);
      }
    } else {
      // Queue for later if offline
      await queueAction(user.id, actionType, tableName, payload, recordId);
      toast({
        title: "Action queued",
        description: "This action will be synced when you're back online",
      });
    }
  };

  return {
    isOnline,
    isSyncing,
    syncingCount,
    syncQueueSize: syncingCount, // Alias for backward compatibility
    queueOfflineAction,
    syncQueuedActions,
    syncPendingChanges: syncQueuedActions, // Alias for backward compatibility
  };
}
